<div class="api-keys-dashboard">
  <!-- Header -->
  <div class="dashboard-header">
    <div class="header-left">
      <h1><i class="fa-solid fa-key"></i> API Keys</h1>
      <span class="subtitle" *ngIf="selectedOrganization">{{ selectedOrganization.Name }}</span>
    </div>
    <div class="header-right">
      <!-- Organization Selector -->
      <div class="org-selector" *ngIf="organizations.length > 1">
        <label>Organization:</label>
        <select
          [ngModel]="selectedOrganization?.ID"
          (ngModelChange)="onOrganizationChange($event)"
        >
          <option *ngFor="let org of organizations" [value]="org.ID">{{ org.Name }}</option>
        </select>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div class="loading-state" *ngIf="isLoading">
    <i class="fa-solid fa-spinner fa-spin"></i>
    <span>Loading API keys...</span>
  </div>

  <!-- Error Message -->
  <div class="error-banner" *ngIf="errorMessage">
    <i class="fa-solid fa-exclamation-triangle"></i>
    {{ errorMessage }}
    <button class="close-btn" (click)="errorMessage = ''">
      <i class="fa-solid fa-times"></i>
    </button>
  </div>

  <!-- Success Message -->
  <div class="success-banner" *ngIf="successMessage">
    <i class="fa-solid fa-check-circle"></i>
    {{ successMessage }}
  </div>

  <!-- Main Content -->
  <div class="dashboard-content" *ngIf="!isLoading && selectedOrganization">
    <!-- Toolbar -->
    <div class="toolbar">
      <div class="toolbar-left">
        <button class="btn btn-primary" (click)="startGenerate()" [disabled]="isGenerating || isSaving">
          <i class="fa-solid fa-plus"></i> Generate New Key
        </button>
      </div>
      <div class="toolbar-right">
        <div class="filter-group">
          <label>Status:</label>
          <select [(ngModel)]="statusFilter">
            <option *ngFor="let status of statusOptions" [value]="status">{{ status }}</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Generate Key Form -->
    <div class="key-card generate-card" *ngIf="isGenerating">
      <div class="card-header">
        <h3><i class="fa-solid fa-wand-magic-sparkles"></i> Generate New API Key</h3>
      </div>
      <div class="card-body">
        <div class="security-notice">
          <i class="fa-solid fa-shield-halved"></i>
          <p>The API key will only be shown once after generation. Make sure to copy it immediately.</p>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>Label *</label>
            <input type="text" [(ngModel)]="generateForm.label" placeholder="e.g., Production API Key" required />
          </div>
          <div class="form-group">
            <label>Expires At (optional)</label>
            <input type="date" [(ngModel)]="generateForm.expiresAt" />
          </div>
        </div>
      </div>
      <div class="card-footer">
        <button class="btn btn-secondary" (click)="cancelGenerate()" [disabled]="isSaving">Cancel</button>
        <button class="btn btn-primary" (click)="generateKey()" [disabled]="isSaving || !generateForm.label.trim()">
          <i class="fa-solid fa-wand-magic-sparkles" *ngIf="!isSaving"></i>
          <i class="fa-solid fa-spinner fa-spin" *ngIf="isSaving"></i>
          Generate Key
        </button>
      </div>
    </div>

    <!-- Keys List -->
    <div class="keys-list">
      <div class="empty-state" *ngIf="filteredKeys.length === 0 && !isGenerating">
        <i class="fa-solid fa-key"></i>
        <p>No API keys found for this organization</p>
        <button class="btn btn-primary" (click)="startGenerate()">
          <i class="fa-solid fa-plus"></i> Generate First Key
        </button>
      </div>

      <div
        class="key-card"
        *ngFor="let display of filteredKeys"
        [class.is-editing]="display.isEditing"
        [class.is-expired]="isExpired(display.apiKey)"
        [class.is-revoked]="display.apiKey.Status === 'Revoked'"
      >
        <!-- View Mode -->
        <ng-container *ngIf="!display.isEditing">
          <div class="card-header">
            <div class="key-info">
              <h3>{{ display.apiKey.Label }}</h3>
              <span class="status-badge" [ngClass]="getStatusClass(display.apiKey.Status)">
                {{ display.apiKey.Status }}
              </span>
              <span class="expired-badge" *ngIf="isExpired(display.apiKey) && display.apiKey.Status === 'Active'">
                Expired
              </span>
            </div>
            <div class="key-buttons">
              <button
                class="btn-icon"
                (click)="toggleSuspend(display)"
                [disabled]="isSaving || display.apiKey.Status === 'Revoked'"
                [title]="display.apiKey.Status === 'Suspended' ? 'Activate' : 'Suspend'"
              >
                <i class="fa-solid" [ngClass]="display.apiKey.Status === 'Suspended' ? 'fa-play' : 'fa-pause'"></i>
              </button>
              <button class="btn-icon" (click)="startEdit(display)" [disabled]="isSaving" title="Edit">
                <i class="fa-solid fa-edit"></i>
              </button>
              <button
                class="btn-icon btn-warning"
                (click)="confirmRevoke(display)"
                [disabled]="isSaving || display.apiKey.Status === 'Revoked'"
                title="Revoke"
              >
                <i class="fa-solid fa-ban"></i>
              </button>
              <button class="btn-icon btn-danger" (click)="confirmDelete(display)" [disabled]="isSaving" title="Delete">
                <i class="fa-solid fa-trash"></i>
              </button>
              <button class="btn-icon" (click)="openKeyRecord(display)" title="Open Record">
                <i class="fa-solid fa-external-link-alt"></i>
              </button>
            </div>
          </div>
          <div class="card-body">
            <div class="key-details">
              <div class="detail-item">
                <label>Hash:</label>
                <code>{{ getMaskedHash(display.apiKey.Hash) }}</code>
              </div>
              <div class="detail-item">
                <label>Created:</label>
                <span>{{ formatDateTime(display.apiKey.__mj_CreatedAt) }}</span>
              </div>
              <div class="detail-item">
                <label>Expires:</label>
                <span [class.expired-text]="isExpired(display.apiKey)">{{ formatDate(display.apiKey.ExpiresAt) }}</span>
              </div>
              <div class="detail-item">
                <label>Last Used:</label>
                <span>{{ formatDateTime(display.apiKey.LastUsedAt) }}</span>
              </div>
            </div>
          </div>
        </ng-container>

        <!-- Edit Mode -->
        <ng-container *ngIf="display.isEditing">
          <div class="card-header">
            <h3><i class="fa-solid fa-edit"></i> Edit API Key</h3>
          </div>
          <div class="card-body">
            <div class="form-row">
              <div class="form-group">
                <label>Label</label>
                <input type="text" [(ngModel)]="display.editForm.label" />
              </div>
              <div class="form-group">
                <label>Status</label>
                <select [(ngModel)]="display.editForm.status">
                  <option value="Active">Active</option>
                  <option value="Suspended">Suspended</option>
                  <option value="Revoked">Revoked</option>
                </select>
              </div>
              <div class="form-group">
                <label>Expires At</label>
                <input type="date" [(ngModel)]="display.editForm.expiresAt" />
              </div>
            </div>
          </div>
          <div class="card-footer">
            <button class="btn btn-secondary" (click)="cancelEdit(display)" [disabled]="isSaving">Cancel</button>
            <button class="btn btn-primary" (click)="saveEdit(display)" [disabled]="isSaving">
              <i class="fa-solid fa-save" *ngIf="!isSaving"></i>
              <i class="fa-solid fa-spinner fa-spin" *ngIf="isSaving"></i>
              Save Changes
            </button>
          </div>
        </ng-container>
      </div>
    </div>
  </div>

  <!-- No Organization Selected -->
  <div class="empty-state" *ngIf="!isLoading && !selectedOrganization">
    <i class="fa-solid fa-building"></i>
    <p>Select an organization to manage API keys</p>
  </div>

  <!-- Generated Key Modal -->
  <div class="modal-overlay" *ngIf="showGeneratedKeyModal" (click)="closeGeneratedKeyModal()">
    <div class="modal-content" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3><i class="fa-solid fa-check-circle success-icon"></i> API Key Generated</h3>
      </div>
      <div class="modal-body">
        <div class="warning-notice">
          <i class="fa-solid fa-exclamation-triangle"></i>
          <p><strong>Important:</strong> This is the only time you will see this key. Copy it now and store it securely.</p>
        </div>
        <div class="key-display">
          <input type="text" [value]="generatedKey" readonly />
          <button class="btn btn-primary" (click)="copyKeyToClipboard()">
            <i class="fa-solid" [ngClass]="copiedToClipboard ? 'fa-check' : 'fa-copy'"></i>
            {{ copiedToClipboard ? 'Copied!' : 'Copy' }}
          </button>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-primary" (click)="closeGeneratedKeyModal()">
          I've Saved the Key
        </button>
      </div>
    </div>
  </div>

  <!-- Confirm Dialog -->
  <div class="confirm-overlay" *ngIf="confirmDialogVisible" (click)="closeConfirmDialog()">
    <div class="confirm-dialog" (click)="$event.stopPropagation()">
      <h3>{{ confirmDialogTitle }}</h3>
      <p>{{ confirmDialogMessage }}</p>
      <div class="dialog-buttons">
        <button class="btn btn-secondary" (click)="closeConfirmDialog()">Cancel</button>
        <button
          class="btn"
          [ngClass]="confirmDialogDanger ? 'btn-danger' : 'btn-primary'"
          (click)="confirmDialogYes()"
        >
          {{ confirmDialogButtonText }}
        </button>
      </div>
    </div>
  </div>
</div>
